import * as React from 'react';
import * as cx from 'classnames';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core';

import Img from 'ha/ui/next/Img';
import Loader from 'ha/components/Loader';
import { withConfig, ConfigProps } from 'ha/helpers/config';
import SvgIcon from 'ha/ui/next/SvgIcon';
import ArrowLeftIcon from 'streamline-icons/arrow-left-1.svg';
import ArrowRightIcon from 'streamline-icons/arrow-right-1.svg';
import NoPhotoImg from 'img-src/static/nophotos.png';

import { shouldFixImage } from './helpers';
import { galleryPhotos } from './constants';

interface ImageDimensions {
  naturalWidth: number;
  naturalHeight: number;
}

interface Props {
  photos: string[];
  alt: string;
  itemProp?: string;
  isLazy?: boolean;
  showDots?: boolean;
}

interface State {
  shouldPreload: boolean;
  active: number;
  photos: string[];
  fixedPhotos: { [key: string]: boolean };
}

const styles = (t: Theme) =>
  createStyles({
    wrapper: {
      cursor: 'pointer',
      '&:hover > *': {
        color: t.palette.white.main,
      },
      '&:hover $arrowPrev': {
        background: 'linear-gradient(90deg, rgba(0,0,0,0.3), transparent)',
      },
      '&:hover $arrowNext': {
        background: 'linear-gradient(270deg, rgba(0,0,0,0.3), transparent)',
      },
    },
    slides: {
      overflow: 'hidden',
      borderRadius: '0.4rem',
      background:
        'linear-gradient(90deg, rgba(3, 3, 3, 0.07) 0, hsla(0,0%,100%,.07) 50%, rgba(3, 3, 3, 0.07))',
    },
    image: {
      display: 'none',
      height: '100%',
      width: 'auto',
      position: 'absolute',
    },
    imageFixed: {
      height: 'auto',
      width: '100%',
    },
    imageActive: {
      display: 'block',
    },
    arrow: {
      position: 'absolute',
      top: 0,
      height: '100%',
      width: '60px',
      color: 'transparent',
      display: 'flex',
      alignItems: 'center',
    },
    arrowVisible: {
      color: t.palette.white.main,
    },
    arrowPrev: {
      left: 0,
      justifyContent: 'flex-start',
      '& > *': {
        marginLeft: '1rem',
      },
      [t.breakpoints.down('md')]: {
        color: t.palette.white.main,
        background: 'linear-gradient(90deg, rgba(0,0,0,0.3), transparent)',
      },
    },
    arrowNext: {
      right: 0,
      justifyContent: 'flex-end',
      '& > *': {
        marginRight: '1rem',
      },
      [t.breakpoints.down('md')]: {
        color: t.palette.white.main,
        background: 'linear-gradient(270deg, rgba(0,0,0,0.3), transparent)',
      },
    },
  });

type ComponentProps = Props & ConfigProps & WithStyles<typeof styles>;

export class ListingPreviewGallery extends React.Component<
  ComponentProps,
  State
> {
  constructor(props: ComponentProps) {
    super(props);

    const photos = props.photos.slice(0, galleryPhotos);

    this.state = {
      shouldPreload: false,
      active: 0,
      photos: photos.length > 0 ? photos : [NoPhotoImg],
      fixedPhotos: {},
    };
  }

  componentDidMount() {
    // After component has mounted, preload next and previous photo.
    this.setState({ shouldPreload: true });
  }

  shouldComponentUpdate(_: Props, nextState: State) {
    const preloadChanged = nextState.shouldPreload !== this.state.shouldPreload;
    const activeChanged = nextState.active !== this.state.active;
    const fixingChanged =
      Object.keys(nextState.fixedPhotos).length !==
      Object.keys(this.state.fixedPhotos).length;

    return preloadChanged || activeChanged || fixingChanged;
  }

  handleImageLoad = (photo: string) => (dimensions: ImageDimensions) => {
    const { fixedPhotos } = this.state;

    this.setState({
      fixedPhotos: {
        ...fixedPhotos,
        [photo]: shouldFixImage(dimensions),
      },
    });
  };

  setSlide = (index: number): React.MouseEventHandler<HTMLDivElement> => e => {
    e.stopPropagation();
    e.preventDefault();

    this.setState({ active: index });
  };

  previousSlide: React.MouseEventHandler<HTMLDivElement> = e => {
    e.stopPropagation();
    e.preventDefault();

    const { active: currentActive, photos } = this.state;

    const active = currentActive === 0 ? photos.length - 1 : currentActive - 1;

    this.setState({ active });
  };

  nextSlide: React.MouseEventHandler<HTMLDivElement> = e => {
    e.stopPropagation();
    e.preventDefault();

    const { active: currentActive, photos } = this.state;

    const active = (currentActive + 1) % photos.length;

    this.setState({ active });
  };

  // We only show placeholder for the active image (not for preloaded images)
  // We hide the placeholder for the first image of non-lazy loaded galleries.
  shouldRenderPlaceholder(
    preloadIndex: number,
    preloadActiveIndex: number,
  ): boolean {
    const { isLazy } = this.props;
    const { active } = this.state;

    const isCurrentImage = preloadIndex === preloadActiveIndex;

    return isCurrentImage && (isLazy || active !== 0);
  }

  render() {
    const { alt, itemProp, appConfig, classes } = this.props;
    const imageTimeoutMs =
      appConfig && appConfig.thumb && appConfig.thumb.imageTimeoutMs;
    const { active, photos, fixedPhotos, shouldPreload } = this.state;

    const previousPhoto = photos[active === 0 ? photos.length - 1 : active - 1];
    const activePhoto = photos[active];
    const nextPhoto = photos[(active + 1) % photos.length];

    // If preload is enabled, render previous and next photo in DOM.
    const preloadedPhotos = shouldPreload
      ? [previousPhoto, activePhoto, nextPhoto]
      : [activePhoto];
    const preloadedActive = preloadedPhotos.indexOf(activePhoto);

    return (
      <div className={classes.wrapper}>
        <div className={classes.slides}>
          {preloadedPhotos.map((photo, index) => (
            <Img
              itemProp={itemProp}
              key={`slide-${photo}`}
              src={photo}
              alt={alt}
              onLoad={this.handleImageLoad(photo)}
              timeoutMs={imageTimeoutMs}
              placeholder={
                this.shouldRenderPlaceholder(index, preloadedActive) ? (
                  <Loader />
                ) : null
              }
              className={cx(classes.image, {
                [classes.imageFixed]: fixedPhotos[photo],
                [classes.imageActive]: index === preloadedActive,
              })}
            />
          ))}
        </div>

        {photos.length > 1 && (
          <React.Fragment>
            <div
              className={cx(classes.arrow, classes.arrowPrev)}
              onClick={this.previousSlide}
              data-test-locator="Previous arrow"
            >
              <SvgIcon>
                <ArrowLeftIcon />
              </SvgIcon>
            </div>
            <div
              className={cx(classes.arrow, classes.arrowNext)}
              onClick={this.nextSlide}
              data-test-locator="Next arrow"
            >
              <SvgIcon>
                <ArrowRightIcon />
              </SvgIcon>
            </div>
          </React.Fragment>
        )}
      </div>
    );
  }
}

export default withConfig(withStyles(styles)(ListingPreviewGallery));
